import type { ReactNode } from 'react';
import { useEditorSelector } from '../editor_context';
import {
   Document,
   Paragraph,
   Text,
   Bold,
   Italic,
   Strike,
   CustomCode,
   EditableLink,
   WikiLink,
   CustomHeading,
   History,
   CustomBulletList,
   CustomOrderedList,
   CustomListItem,
   Blockquote,
   CustomMention,
   CodeBlock,
   MediaManager,
   CustomHardBreak,
   CustomSubscript,
   CustomSuperscript,
   CustomUnderline,
   Video,
   Table,
   FeaturedQuote,
   Note,
   Grid,
   WikiImage,
   ImageUpload,
} from '../extensions';
import { MenuSeparator } from '../components/MenuSeparator';
import { TiptapMenubar } from '../components/TiptapMenubar';
import { TextStyleDropdown } from '../components/TextStyleDropdown';
import { VideoInsertDropdown } from '../components/VideoInsertDropdown';
import {
   BoldButton,
   ItalicButton,
   CodeButton,
   LinkButton,
   CodeBlockButton,
   BlockquoteButton,
   NoteDropdown,
   BulletListButton,
   OrderedListButton,
   IndentButton,
   UnindentButton,
   UndoButton,
   RedoButton,
   InsertImageButton,
   ImageTools,
   GridDropdown,
   GridAddColumnButton,
   GridRemoveColumnButton,
} from '../components/buttons';
import type { TiptapExtension } from '.';

export const WikiExtensions: Array<TiptapExtension> = [
   Document,
   Paragraph,
   Text,
   Bold,
   Italic,
   Strike,
   CustomCode,
   EditableLink,
   WikiLink,
   CustomHeading,
   History,
   CustomBulletList,
   CustomOrderedList,
   CustomListItem,
   Blockquote,
   CustomMention,
   CodeBlock,
   MediaManager,
   CustomHardBreak,
   CustomSubscript,
   CustomSuperscript,
   CustomUnderline,
   Video,
   Table,
   FeaturedQuote,
   Note,
   Grid,
   WikiImage,
   ImageUpload,
];

export const WikiMenuBar = ({ menubarEnd }: { menubarEnd?: ReactNode }) => {
   const canIUseGridTools = App.canIUseGridTools;

   return (
      <TiptapMenubar menubarEnd={menubarEnd}>
         <BoldButton />
         <ItalicButton />
         <CodeButton />
         <LinkButton />
         <MenuSeparator />
         <TextStyleDropdown />
         <MenuSeparator />
         <CodeBlockButton />
         <BlockquoteButton />
         <NoteDropdown />
         <BulletListButton />
         <OrderedListButton />
         <UnindentButton />
         <IndentButton />
         {canIUseGridTools && (
            <>
               <MenuSeparator />
               <GridTools />
            </>
         )}
         <MenuSeparator />
         <div className="inline-flex">
            <InsertImageButton />
            <ImageTools />
         </div>
         <VideoInsertDropdown />
         <MenuSeparator />
         <UndoButton />
         <RedoButton />
      </TiptapMenubar>
   );
};

function GridTools() {
   const gridColumns = useGridColumns();
   const showGridTools = gridColumns !== null;
   if (!showGridTools) {
      return <GridDropdown />;
   }

   return (
      <>
         <GridDropdown />
         <GridAddColumnButton />
         <GridRemoveColumnButton />
      </>
   );
}

function useGridColumns() {
   // useEditorSelector returns null for a destroyed editor, which also covers
   // the concurrent render that outlives it (see #63212).
   return useEditorSelector(editor =>
      editor.isActive('grid') ? editor.getAttributes('grid')?.columns : null
   );
}
